import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api.js';
import ConfirmModal from '../../components/ConfirmModal.jsx';
import { money } from '../../utils/money.js';
import { BILLING_INCREMENTS, billableHours, billingRuleText, formatDuration } from '../../utils/billingRule.js';

/**
 * Service tariffs (8950): a rate per service, port and — optionally — assistance
 * area, as in the client's tariff sheets. A rate is a lump sum for the whole
 * service (shared across its jobs on the invoice) or an hourly rate per tug with
 * a minimum of billed hours. An area's own rate beats the port-wide one; a
 * booking whose service has no rate here is priced on the hourly rate cards.
 */

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = {
  id: null,
  serviceId: '',
  portId: '',
  assistanceAreaId: '',
  rateBasis: 'HourlyPerTug',
  amount: '',
  // 9034: a 1-hour minimum by default, then billing increments per the client tariff.
  minHours: '1',
  billingIncrementMinutes: '',
  standbyHourlyRate: '',
  additionalTugAmount: '',
  vatPercent: '12',
  currency: 'PHP',
  activeFrom: today(),
  activeTo: '',
};

const ruleOf = (form) => ({
  minHours: form.minHours === '' ? 0 : Number(form.minHours) || 0,
  incrementMinutes: form.billingIncrementMinutes ? Number(form.billingIncrementMinutes) : null,
});

const STATUS_BADGE ={ Active: 'bg-success', Scheduled: 'bg-info text-dark', Expired: 'bg-secondary' };

export default function ServiceRatesCard() {
  const [rates, setRates] = useState([]);
  const [services, setServices] = useState([]);
  const [ports, setPorts] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    const [r, s, p, a] = await Promise.all([
      api.listServiceRates(),
      api.listServices(),
      api.listServicePorts(),
      api.listAssistanceAreas(),
    ]);
    setRates(r || []);
    setServices(s || []);
    setPorts(p || []);
    setAreas(a || []);
  }, []);

  useEffect(() => {
    load().catch((err) => setLoadError(err.message || 'Failed to load service tariffs.'));
  }, [load]);

  const areasForPort = useMemo(() => areas.filter((a) => form && a.portId === form.portId), [areas, form]);

  const setField = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(patch).forEach((k) => delete next[k]);
      delete next.form;
      return next;
    });
  };

  const openNew = () => {
    setErrors({});
    setForm({ ...EMPTY, activeFrom: today() });
  };

  const openEdit = (r) => {
    setErrors({});
    setForm({
      id: r.id,
      serviceId: r.serviceId,
      portId: r.portId,
      assistanceAreaId: r.assistanceAreaId || '',
      rateBasis: r.rateBasis,
      amount: String(r.amount),
      minHours: r.minHours ? String(r.minHours) : '',
      billingIncrementMinutes: r.billingIncrementMinutes ? String(r.billingIncrementMinutes) : '',
      standbyHourlyRate: r.standbyHourlyRate != null ? String(r.standbyHourlyRate) : '',
      additionalTugAmount: r.additionalTugAmount != null ? String(r.additionalTugAmount) : '',
      vatPercent: String(r.vatPercent),
      currency: r.currency,
      activeFrom: r.activeFrom,
      activeTo: r.activeTo || '',
    });
  };

  const validate = () => {
    const next = {};
    if (!form.serviceId) next.serviceId = 'Select a service';
    if (!form.portId) next.portId = 'Select a port';
    if (form.amount === '' || !(Number(form.amount) > 0)) next.amount = 'Rate must be greater than 0';
    if (form.rateBasis === 'HourlyPerTug' && form.minHours !== '') {
      const h = Number(form.minHours);
      if (Number.isNaN(h) || h < 0) next.minHours = 'Minimum hours cannot be negative';
      else if (h > 48) next.minHours = 'Minimum hours cannot exceed 48';
    }
    if (form.standbyHourlyRate !== '' && !(Number(form.standbyHourlyRate) >= 0)) {
      next.standbyHourlyRate = 'Standby rate cannot be negative';
    }
    if (form.rateBasis === 'LumpSum' && form.additionalTugAmount !== '' && !(Number(form.additionalTugAmount) >= 0)) {
      next.additionalTugAmount = 'Additional tug charge cannot be negative';
    }
    const vat = Number(form.vatPercent);
    if (form.vatPercent === '' || Number.isNaN(vat) || vat < 0) next.vatPercent = 'VAT cannot be negative';
    else if (vat > 100) next.vatPercent = 'VAT cannot exceed 100%';
    if (!form.activeFrom) next.activeFrom = 'Choose the date the rate starts';
    if (form.activeTo && form.activeFrom && form.activeTo < form.activeFrom) {
      next.activeTo = 'The end date must be on or after the start date';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (!validate()) return;
    setSaving(true);
    const payload = {
      serviceId: form.serviceId,
      portId: form.portId,
      assistanceAreaId: form.assistanceAreaId || null,
      rateBasis: form.rateBasis,
      amount: Number(form.amount),
      minHours: form.rateBasis === 'HourlyPerTug' && form.minHours !== '' ? Number(form.minHours) : 0,
      billingIncrementMinutes:
        form.rateBasis === 'HourlyPerTug' && form.billingIncrementMinutes !== ''
          ? Number(form.billingIncrementMinutes)
          : null,
      standbyHourlyRate: form.standbyHourlyRate === '' ? null : Number(form.standbyHourlyRate),
      additionalTugAmount:
        form.rateBasis === 'LumpSum' && form.additionalTugAmount !== '' ? Number(form.additionalTugAmount) : null,
      vatPercent: Number(form.vatPercent),
      currency: form.currency,
      activeFrom: form.activeFrom,
      activeTo: form.activeTo || null,
    };
    try {
      const saved = form.id ? await api.updateServiceRate(form.id, payload) : await api.createServiceRate(payload);
      setForm(null);
      setMsg({
        type: 'success',
        text: `Service tariff saved: ${saved.serviceName} at ${saved.portName}${saved.areaName ? ` — ${saved.areaName}` : ''}.`,
      });
      await load();
    } catch (err) {
      const fieldErrors = Object.fromEntries((err.details || []).map((d) => [d.path, d.message]));
      setErrors({ ...fieldErrors, form: err.message || 'Failed to save the service tariff.' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await api.deleteServiceRate(confirmDel.id);
      setMsg({ type: 'success', text: 'Service tariff deleted.' });
      setConfirmDel(null);
      await load();
    } catch (err) {
      setMsg({ type: 'danger', text: err.message || 'Failed to delete the service tariff.' });
      setConfirmDel(null);
    } finally {
      setDeleting(false);
    }
  };

  const cls = (key, base = 'form-control form-control-sm') => `${base}${errors[key] ? ' is-invalid' : ''}`;
  const err = (key) => errors[key] && <div className="invalid-feedback">{errors[key]}</div>;

  return (
    <div className="card border-0 shadow-sm mb-4" data-testid="service-rates">
      <div className="card-header bg-white pt-4 border-bottom-0 d-flex flex-wrap gap-2 justify-content-between align-items-center">
        <div>
          <h5 className="mb-0 text-secondary">
            <i className="fas fa-map-signs me-2" aria-hidden="true" />
            Service Tariffs by Port &amp; Assistance Area
          </h5>
          <p className="small text-muted mb-0">
            A lump sum per service, or an hourly rate per tug. An area&apos;s own rate beats the port-wide rate; services
            without a rate here are priced on the hourly rate cards.
          </p>
        </div>
        <button type="button" className="btn btn-sm btn-primary" onClick={openNew} data-testid="new-service-rate">
          <i className="fas fa-plus me-1" aria-hidden="true" />
          Add Service Tariff
        </button>
      </div>
      <div className="card-body p-0">
        {loadError && <div className="alert alert-danger m-3">{loadError}</div>}
        {msg && (
          <div className={`alert alert-${msg.type} m-3`} role="alert" data-testid="service-rate-message">
            {msg.text}
          </div>
        )}
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="bg-light text-secondary small text-uppercase">
              <tr>
                <th>Service</th>
                <th>Port</th>
                <th>Assistance area</th>
                <th>Basis</th>
                <th className="text-end">Rate</th>
                <th>VAT</th>
                <th>Active window</th>
                <th>Status</th>
                <th aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {rates.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center text-muted py-4">
                    No service tariffs yet.
                  </td>
                </tr>
              )}
              {rates.map((r) => (
                <tr key={r.id} data-testid="service-rate-row" data-service={r.serviceName} data-area={r.areaName || ''}>
                  <td className="fw-bold">{r.serviceName}</td>
                  <td>{r.portName}</td>
                  <td>{r.areaName || <span className="text-muted">Whole port</span>}</td>
                  <td className="small">{r.rateBasisLabel}</td>
                  <td className="text-end text-nowrap" data-testid="service-rate-amount">
                    {money(r.amount, r.currency)}
                    {r.rateBasis === 'HourlyPerTug' && (
                      <small className="d-block text-muted">
                        per tug-hour{r.minHours ? ` · min ${r.minHours}h` : ''}
                        {/* 9034 */}
                        {r.billingIncrementMinutes ? (
                          <span data-testid="service-rate-increment"> · {r.billingIncrementMinutes}-min increments</span>
                        ) : null}
                      </small>
                    )}
                    {r.standbyHourlyRate != null && (
                      <small className="d-block text-muted">standby {money(r.standbyHourlyRate, r.currency)}/h</small>
                    )}
                    {r.rateBasis === 'LumpSum' && (
                      <small className="d-block text-muted" data-testid="service-rate-additional-tug">
                        added tug{' '}
                        {r.additionalTugAmount != null ? money(r.additionalTugAmount, r.currency) : "one tug's share"}
                      </small>
                    )}
                  </td>
                  <td>{r.vatPercent}%</td>
                  <td className="small">
                    {r.activeFrom} → {r.activeTo || '—'}
                  </td>
                  <td>
                    <span className={`badge ${STATUS_BADGE[r.status] || 'bg-light text-dark'}`}>{r.status}</span>
                  </td>
                  <td className="text-end text-nowrap">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary me-1"
                      onClick={() => openEdit(r)}
                      data-testid="edit-service-rate"
                    >
                      <i className="fas fa-pen me-1" aria-hidden="true" />
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => setConfirmDel(r)}
                      data-testid="delete-service-rate"
                    >
                      <i className="fas fa-trash" aria-hidden="true" />
                      <span className="visually-hidden">Delete</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {form && (
        <>
          <div className="modal d-block" tabIndex={-1} role="dialog" data-testid="service-rate-modal">
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <form onSubmit={save} noValidate>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      <i className="fas fa-map-signs me-2 text-primary" aria-hidden="true" />
                      {form.id ? 'Edit Service Tariff' : 'Add Service Tariff'}
                    </h5>
                    <button type="button" className="btn-close" aria-label="Close" onClick={() => setForm(null)} />
                  </div>
                  <div className="modal-body">
                    {errors.form && (
                      <div className="alert alert-danger" role="alert" data-testid="service-rate-error">
                        {errors.form}
                      </div>
                    )}
                    <div className="row g-2">
                      <div className="col-md-4 mb-2">
                        <label htmlFor="sr-service" className="form-label small fw-bold text-muted">
                          Service <span className="text-danger">*</span>
                        </label>
                        <select
                          id="sr-service"
                          className={cls('serviceId', 'form-select form-select-sm')}
                          value={form.serviceId}
                          onChange={(e) => setField({ serviceId: e.target.value })}
                          data-testid="sr-service"
                        >
                          <option value="">Select…</option>
                          {/* 8984: only active services take tariffs; an existing rate keeps its own. */}
                          {services
                            .filter((s) => s.active || s.id === form.serviceId)
                            .map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.name}
                                {s.active ? '' : ' (inactive)'}
                              </option>
                            ))}
                        </select>
                        {err('serviceId')}
                      </div>
                      <div className="col-md-4 mb-2">
                        <label htmlFor="sr-port" className="form-label small fw-bold text-muted">
                          Port <span className="text-danger">*</span>
                        </label>
                        <select
                          id="sr-port"
                          className={cls('portId', 'form-select form-select-sm')}
                          value={form.portId}
                          onChange={(e) => setField({ portId: e.target.value, assistanceAreaId: '' })}
                          data-testid="sr-port"
                        >
                          <option value="">Select…</option>
                          {ports.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </select>
                        {err('portId')}
                      </div>
                      <div className="col-md-4 mb-2">
                        <label htmlFor="sr-area" className="form-label small fw-bold text-muted">
                          Assistance area
                        </label>
                        <select
                          id="sr-area"
                          className={cls('assistanceAreaId', 'form-select form-select-sm')}
                          value={form.assistanceAreaId}
                          onChange={(e) => setField({ assistanceAreaId: e.target.value })}
                          disabled={!form.portId}
                          data-testid="sr-area"
                        >
                          <option value="">Whole port (any area)</option>
                          {areasForPort.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.name}
                            </option>
                          ))}
                        </select>
                        {err('assistanceAreaId')}
                      </div>
                    </div>
                    <div className="row g-2">
                      <div className="col-md-4 mb-2">
                        <label htmlFor="sr-basis" className="form-label small fw-bold text-muted">
                          Rate basis <span className="text-danger">*</span>
                        </label>
                        <select
                          id="sr-basis"
                          className="form-select form-select-sm"
                          value={form.rateBasis}
                          onChange={(e) =>
                            setField({
                              rateBasis: e.target.value,
                              minHours: e.target.value === 'HourlyPerTug' ? '1' : '',
                              billingIncrementMinutes: '',
                            })
                          }
                          data-testid="sr-basis"
                        >
                          <option value="HourlyPerTug">Hourly per tug</option>
                          <option value="LumpSum">Lump sum per service</option>
                        </select>
                      </div>
                      <div className="col-md-4 mb-2">
                        <label htmlFor="sr-amount" className="form-label small fw-bold text-muted">
                          {form.rateBasis === 'LumpSum' ? 'Lump sum' : 'Rate per tug-hour'} <span className="text-danger">*</span>
                        </label>
                        <input
                          id="sr-amount"
                          type="number"
                          min="0"
                          step="0.01"
                          className={cls('amount')}
                          value={form.amount}
                          onChange={(e) => setField({ amount: e.target.value })}
                          data-testid="sr-amount"
                        />
                        {err('amount')}
                      </div>
                      <div className="col-md-4 mb-2">
                        <label htmlFor="sr-currency" className="form-label small fw-bold text-muted">
                          Currency
                        </label>
                        <select
                          id="sr-currency"
                          className="form-select form-select-sm"
                          value={form.currency}
                          onChange={(e) => setField({ currency: e.target.value })}
                          data-testid="sr-currency"
                        >
                          <option value="PHP">PHP</option>
                          <option value="USD">USD</option>
                        </select>
                      </div>
                    </div>
                    <div className="row g-2">
                      {form.rateBasis === 'HourlyPerTug' && (
                        <div className="col-md-4 mb-2">
                          <label htmlFor="sr-minhours" className="form-label small fw-bold text-muted">
                            Minimum hours per tug
                          </label>
                          <input
                            id="sr-minhours"
                            type="number"
                            min="0"
                            step="0.5"
                            className={cls('minHours')}
                            value={form.minHours}
                            onChange={(e) => setField({ minHours: e.target.value })}
                            data-testid="sr-minhours"
                          />
                          {err('minHours')}
                        </div>
                      )}
                      {/* 9034: time beyond the minimum is billed in these increments */}
                      {form.rateBasis === 'HourlyPerTug' && (
                        <div className="col-md-4 mb-2">
                          <label htmlFor="sr-increment" className="form-label small fw-bold text-muted">
                            Billing increment
                          </label>
                          <select
                            id="sr-increment"
                            className={cls('billingIncrementMinutes', 'form-select form-select-sm')}
                            value={form.billingIncrementMinutes}
                            onChange={(e) => setField({ billingIncrementMinutes: e.target.value })}
                            data-testid="sr-increment"
                          >
                            <option value="">None (actual time)</option>
                            {[...new Set([...BILLING_INCREMENTS, Number(form.billingIncrementMinutes) || 15])]
                              .sort((a, b) => a - b)
                              .map((m) => (
                                <option key={m} value={String(m)}>
                                  {m === 60 ? 'Every hour (60 min)' : `Every ${m} min`}
                                </option>
                              ))}
                          </select>
                          {err('billingIncrementMinutes')}
                        </div>
                      )}
                      <div className="col-md-4 mb-2">
                        <label htmlFor="sr-standby" className="form-label small fw-bold text-muted">
                          Standby rate per hour
                        </label>
                        <input
                          id="sr-standby"
                          type="number"
                          min="0"
                          step="0.01"
                          className={cls('standbyHourlyRate')}
                          placeholder={form.rateBasis === 'LumpSum' ? 'Blank = not charged' : 'Blank = hourly rate'}
                          value={form.standbyHourlyRate}
                          onChange={(e) => setField({ standbyHourlyRate: e.target.value })}
                          data-testid="sr-standby"
                        />
                        {err('standbyHourlyRate')}
                      </div>
                      {form.rateBasis === 'LumpSum' && (
                        <div className="col-md-4 mb-2">
                          <label htmlFor="sr-additional-tug" className="form-label small fw-bold text-muted">
                            Charge per added tug
                          </label>
                          <input
                            id="sr-additional-tug"
                            type="number"
                            min="0"
                            step="0.01"
                            className={cls('additionalTugAmount')}
                            placeholder="Blank = one tug's share of the lump sum"
                            value={form.additionalTugAmount}
                            onChange={(e) => setField({ additionalTugAmount: e.target.value })}
                            data-testid="sr-additional-tug"
                          />
                          <div className="form-text">Charged on top for each tug the dispatcher adds to the service.</div>
                          {err('additionalTugAmount')}
                        </div>
                      )}
                      <div className="col-md-4 mb-2">
                        <label htmlFor="sr-vat" className="form-label small fw-bold text-muted">
                          VAT %
                        </label>
                        <input
                          id="sr-vat"
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          className={cls('vatPercent')}
                          value={form.vatPercent}
                          onChange={(e) => setField({ vatPercent: e.target.value })}
                          data-testid="sr-vat"
                        />
                        {err('vatPercent')}
                      </div>
                    </div>
                    {form.rateBasis === 'HourlyPerTug' && (
                      <div className="alert alert-light border small py-2" data-testid="sr-billing-rule">
                        <i className="fas fa-stopwatch me-1 text-muted" aria-hidden="true" />
                        Billed per tug: {billingRuleText(ruleOf(form))}. For example,{' '}
                        {[70, 130].map((mins, i) => (
                          <span key={mins}>
                            {i > 0 && '; '}
                            {formatDuration(mins)} actual → {billableHours(mins / 60, ruleOf(form))}h billed
                          </span>
                        ))}
                        .
                      </div>
                    )}
                    <div className="row g-2">
                      <div className="col-md-4 mb-2">
                        <label htmlFor="sr-from" className="form-label small fw-bold text-muted">
                          Active from <span className="text-danger">*</span>
                        </label>
                        <input
                          id="sr-from"
                          type="date"
                          className={cls('activeFrom')}
                          value={form.activeFrom}
                          onChange={(e) => setField({ activeFrom: e.target.value })}
                          data-testid="sr-from"
                        />
                        {err('activeFrom')}
                      </div>
                      <div className="col-md-4 mb-2">
                        <label htmlFor="sr-to" className="form-label small fw-bold text-muted">
                          Active to
                        </label>
                        <input
                          id="sr-to"
                          type="date"
                          className={cls('activeTo')}
                          value={form.activeTo}
                          onChange={(e) => setField({ activeTo: e.target.value })}
                          data-testid="sr-to"
                        />
                        {err('activeTo')}
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setForm(null)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={saving} data-testid="save-service-rate">
                      {saving && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />}
                      Save Tariff
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}

      <ConfirmModal
        open={Boolean(confirmDel)}
        title="Delete service tariff"
        message={
          confirmDel && (
            <>
              Delete the {confirmDel.serviceName} tariff for {confirmDel.portName}
              {confirmDel.areaName ? ` — ${confirmDel.areaName}` : ''}? Bookings not yet invoiced will be priced on the
              next matching rate.
            </>
          )
        }
        busy={deleting}
        onConfirm={remove}
        onCancel={() => setConfirmDel(null)}
      />
    </div>
  );
}
